import type { Customer, EstimateRules, Layout, Maker, Pattern, Process, Product, Proposal, Revision, Space, SyncOp, SyncPullResponse } from "@el/schema";
import type { LocalStore } from "@el/sync";
import Dexie, { type Table } from "dexie";

/** 送信待ちの列の1行。seq で古い順に並べる */
export type OutboxRow = { seq?: number; opId: string; op: SyncOp; error: string | null; addedAt: string };
export type MetaRow = { key: string; value: string };
/** レイアウト編集の書きかけ（保存する前に画面を閉じても消えないように） */
export type DraftRow = {
  proposalId: string;
  baseRevisionId: string | null;
  space: Space | null;
  layout: Layout;
  pendingSteps: { key: string; processId: string | null; after?: string | null }[];
  savedAt: string;
};

export class ElDatabase extends Dexie {
  meta!: Table<MetaRow, string>;
  makers!: Table<Maker, string>;
  processes!: Table<Process, string>;
  products!: Table<Product, string>;
  patterns!: Table<Pattern, string>;
  estimateRules!: Table<EstimateRules, string>;
  customers!: Table<Customer, string>;
  proposals!: Table<Proposal, string>;
  revisions!: Table<Revision, string>;
  outbox!: Table<OutboxRow, number>;
  drafts!: Table<DraftRow, string>;

  constructor(name = "equipment-layout") {
    super(name);
    this.version(1).stores({
      meta: "key",
      makers: "id",
      processes: "id, sortOrder",
      products: "id, status, code",
      patterns: "id",
      estimateRules: "id",
      customers: "id, updatedAt",
      proposals: "id, customerId, updatedAt",
      revisions: "id, proposalId, createdAt",
      outbox: "++seq, &opId",
      drafts: "proposalId",
    });
  }
}

/** 同期で配られる「消えたマスタ」の entity 名 → 表 */
const DELETED_TABLE: Record<string, "makers" | "processes" | "products" | "patterns" | "estimateRules" | "customers" | "proposals"> = {
  maker: "makers",
  makers: "makers",
  process: "processes",
  processes: "processes",
  product: "products",
  products: "products",
  pattern: "patterns",
  patterns: "patterns",
  estimateRules: "estimateRules",
  estimate_rules: "estimateRules",
  estimateRule: "estimateRules",
  customer: "customers",
  customers: "customers",
  proposal: "proposals",
  proposals: "proposals",
};

/** @el/sync の LocalStore を IndexedDB（Dexie）で実装したもの */
export class DexieLocalStore implements LocalStore {
  constructor(readonly db: ElDatabase) {}

  async getMeta(key: "serverSeq" | "lastSyncAt" | "userId"): Promise<string | null> {
    return (await this.db.meta.get(key))?.value ?? null;
  }
  async setMeta(key: string, value: string): Promise<void> {
    await this.db.meta.put({ key, value });
  }

  async applyPull(res: SyncPullResponse): Promise<void> {
    const d = this.db;
    await d.transaction("rw", [d.meta, d.makers, d.processes, d.products, d.patterns, d.estimateRules, d.customers, d.proposals, d.revisions, d.outbox, d.drafts], async () => {
      if (res.wipe) {
        await Promise.all([d.meta, d.makers, d.processes, d.products, d.patterns, d.estimateRules, d.customers, d.proposals, d.revisions, d.outbox, d.drafts].map((t) => t.clear()));
        return;
      }
      await d.makers.bulkPut(res.makers);
      await d.processes.bulkPut(res.processes);
      // 廃止された設備は端末から消さず、状態だけ変える（過去の版で「廃止」の印を出すため）
      await d.products.bulkPut(res.products);
      await d.patterns.bulkPut(res.patterns);
      await d.estimateRules.bulkPut(res.estimateRules);
      // 送信待ちのある顧客・提案・版は、端末の値を残す（まだサーバーに届いていない変更を消さない）
      const pending = { customer: new Set<string>(), proposal: new Set<string>(), revision: new Set<string>() };
      await d.outbox.each((row) => pending[row.op.entity].add(row.op.data.id));
      await d.customers.bulkPut(res.customers.filter((x) => !pending.customer.has(x.id)));
      await d.proposals.bulkPut(res.proposals.filter((x) => !pending.proposal.has(x.id)));
      await d.revisions.bulkPut(res.revisions.filter((x) => !pending.revision.has(x.id)));
      for (const del of res.deleted) {
        const table = DELETED_TABLE[del.entity];
        if (table) await d[table].delete(del.id);
      }
      await d.meta.put({ key: "serverSeq", value: String(res.serverSeq) });
    });
  }

  async enqueue(op: SyncOp): Promise<void> {
    // 同じ opId を2回積まない（画面の二重押し）
    const exists = await this.db.outbox.where("opId").equals(op.opId).count();
    if (exists) return;
    await this.db.outbox.add({ opId: op.opId, op, error: null, addedAt: op.clientTs });
  }
  async peekOutbox(limit: number): Promise<SyncOp[]> {
    if (limit <= 0) return [];
    const rows = await this.db.outbox.orderBy("seq").limit(limit).toArray();
    return rows.map((r) => r.op);
  }
  async removeFromOutbox(opIds: string[]): Promise<void> {
    if (!opIds.length) return;
    await this.db.outbox.where("opId").anyOf(opIds).delete();
  }
  async markOutboxError(opId: string, message: string): Promise<void> {
    await this.db.outbox.where("opId").equals(opId).modify({ error: message });
  }
  async outboxCount(): Promise<number> {
    return this.db.outbox.count();
  }

  async upsertCustomer(c: Customer): Promise<void> {
    await this.db.customers.put(c);
  }
  async upsertProposal(p: Proposal): Promise<void> {
    await this.db.proposals.put(p);
  }
  async addRevision(r: Revision): Promise<void> {
    // 版は追記だけ。同期で番号が決まったものはそのまま上書きしてよい
    await this.db.revisions.put(r);
  }
  /** サーバーが受け付けなかった版を消す（無ければ何もしない） */
  async deleteRevision(id: string): Promise<void> {
    await this.db.revisions.delete(id);
  }
  async clearAll(): Promise<void> {
    const d = this.db;
    await d.transaction("rw", d.tables, async () => {
      await Promise.all(d.tables.map((t) => t.clear()));
    });
  }

  /** fn の中の書き込みを 1 つのトランザクションにする（例外なら全部取り消す。入れ子は外側にまとめる） */
  async transaction(fn: () => Promise<void>): Promise<void> {
    await this.db.transaction("rw", this.db.tables, fn);
  }

  /* ===== Web だけで使う ===== */
  async outboxErrors(): Promise<OutboxRow[]> {
    return (await this.db.outbox.orderBy("seq").toArray()).filter((r) => r.error);
  }
}

let shared: { db: ElDatabase; store: DexieLocalStore } | null = null;
export function getLocal() {
  if (!shared) {
    const db = new ElDatabase();
    shared = { db, store: new DexieLocalStore(db) };
  }
  return shared;
}
